import { SkuKitProductGridDTO } from '../../../orders/products/orderProducts.types';
import { AvailabilityEnum } from '../../../products/ProductTypes';
import {
  EmptySkuDTO,
  FutureStockProductDTO,
  FutureStockSkuDTO,
  InfinitePreOrderProductDTO,
  InfinitePreOrderSkuDTO,
  ProductEmptyDTO,
  ProductReadyForDeliveryDTO,
  ReadyForDeliverySkuDTO,
} from '../../../products/details/productDetails.types';
import { isPreOrder } from '../../../products/utils/availability.utils';
import { OfflineDatabaseKit } from '../../database/offlineDatabase/types/offlineDatabaseKit.types';
import { getOfflineProductColorPacksBasedOnAvailability } from '../../database/offlineDatabase/types/offlineDatabaseProductColor.types';
import { OfflineOrder } from '../../domain/offlineOrder/OfflineOrder';
import { OfflineOrderProduct } from '../../domain/offlineOrder/orderProduct/OfflineOrderProduct';
import { offlineDeliveryWindowsRepository } from '../../repositories/offlineDeliveryWindows.repository';
import { offlineFutureStocksRepository } from '../../repositories/offlineFutureStocks.repository';
import { offlineKitsRepository } from '../../repositories/offlineKits.repository';
import { offlineProductColorPriceInfoRepository } from '../../repositories/offlineProductColorPriceInfo.repository';
import { offlineProductColorsRepository } from '../../repositories/offlineProductColors.repository';
import { offlineSizesRepository } from '../../repositories/offlineSizes.repository';
import { offlineSkusRepository } from '../../repositories/offlineSkus.repository';
import { offlineStocksRepository } from '../../repositories/offlineStocks.repository';
import { OfflineDeliveryWindowTransformers } from '../../transformers/OfflineDatabaseDeliveryWindow.transformers';
import { OfflineSizeTransformers } from '../../transformers/OfflineDatabaseSize.transformers';
import { OfflineOrderProductTransformers } from '../../transformers/OfflineOrderProduct.transformers';
import { OfflineProductColorTransformers } from '../../transformers/productColor/OfflineDatabaseProductColor.transformers';
import { OfflineSkuPriceHandler } from '../utils/OfflineSkuPriceHandler.util';
import {
  GetSkuKitFromSkuIdParams,
  OfflineDatabaseSkuWithItemId,
  OfflineGridBuilderArtifacts,
  OfflineGridBuilderConstructorParams,
} from './offlineGrid.builder.types';

export class OfflineGridBuilder {
  private productColorId: string;
  private availability: AvailabilityEnum;

  private order?: OfflineOrder;
  private priceTableId?: string;
  private deliveryWindowId?: string;
  private kitId?: string;

  constructor({
    priceTableId,
    offlineOrder,
    availability,
    deliveryWindowId,
    productColorId,
    kitId,
  }: OfflineGridBuilderConstructorParams) {
    this.priceTableId = priceTableId;
    this.availability = availability;
    this.deliveryWindowId = deliveryWindowId;
    this.productColorId = productColorId;
    this.order = offlineOrder;
    this.kitId = kitId;
  }

  build() {
    switch (this.availability) {
      case AvailabilityEnum.INFINITE_PRE_ORDER:
        return this.buildInfinitePreOrderProductDTO();
      case AvailabilityEnum.FUTURE_STOCK:
        return this.buildFutureStockProductDTO();
      case AvailabilityEnum.READY_FOR_DELIVERY:
        return this.buildReadyForDeliveryProductDTO();
      case AvailabilityEnum.EMPTY:
        return this.buildEmptyProductDTO();
      default:
        throw new Error('availability not found');
    }
  }

  private getGridSkuKits(kit: OfflineDatabaseKit) {
    const skuKitsFromGrid = kit.skuKits.filter(
      (skuKit) =>
        skuKit.productColor.id === this.productColorId &&
        skuKit.availability === this.availability &&
        skuKit.deliveryWindow?.id === this.deliveryWindowId,
    );

    if (skuKitsFromGrid.length === 0) {
      return [];
    }

    return skuKitsFromGrid;
  }

  private getSkuKitFromSkuId({
    skuId,
    skuKits,
    kit,
  }: GetSkuKitFromSkuIdParams): SkuKitProductGridDTO[] {
    const skuKit = skuKits.find((skuKit) => skuKit.sku.id === skuId);

    if (!skuKit || !kit) {
      return [];
    }

    const formattedSkuKit: SkuKitProductGridDTO = {
      id: skuKit.id,
      kitId: kit.id,
      kitName: kit.name,
      quantity: skuKit.quantity,
    };

    return [formattedSkuKit];
  }

  private async buildFutureStockProductDTO(): Promise<FutureStockProductDTO> {
    const {
      deliveryWindow,
      offlineSkus,
      productColor,
      productColorPriceInfo,
      productSizes,
      hasValidPacks,
      orderProductPack,
    } = await this.getArtifacts();

    if (!deliveryWindow || !this.deliveryWindowId) {
      throw new Error('delivery window not found');
    }

    const futureStocks =
      await offlineFutureStocksRepository.findByProductColorDeliveryWindowAndSkuIds({
        deliveryWindowId: this.deliveryWindowId,
        skuIds: offlineSkus.map((sku) => sku.id),
        productColorId: this.productColorId,
      });

    const kit = this.kitId ? await offlineKitsRepository.getById(this.kitId) : undefined;
    const gridSkuKits = kit ? this.getGridSkuKits(kit) : [];

    const futureStockSkuDTOs = offlineSkus.map<FutureStockSkuDTO>((offlineSku) => {
      const existentFutureStock = futureStocks.find((stock) => stock.skuId === offlineSku.id);

      const available = !!existentFutureStock && existentFutureStock.availableAmount > 0;
      const futureStock = existentFutureStock || {
        id: 'some-id',
        totalAmount: 0,
        availableAmount: 0,
        endDate: new Date(),
        skuId: offlineSku.id,
        startDate: new Date(),
        productId: offlineSku.productId,
        deliveryWindowId: this.deliveryWindowId,
        productColorId: this.productColorId,
      };

      const prices = OfflineSkuPriceHandler.getPricesBySize(
        productColorPriceInfo,
        offlineSku.sizeId,
      );
      const skuKit = this.getSkuKitFromSkuId({
        skuId: offlineSku.id,
        skuKits: gridSkuKits,
        kit: kit,
      });

      return {
        skuKits: skuKit,
        orderRetailPrice: prices?.retailPrice,
        priceComposition: prices?.priceComposition,
        orderWholeSalePrice: prices?.wholesalePrice,
        orderPromotionalPrice: prices?.promotionalPrice,
        available: available,
        availableForSale: available,
        deliveryWindowAvailable: available,
        settledQuantity: 0,
        pendingQuantity: 0,
        reservedQuantity: 0,
        canceledQuantity: 0,
        color: productColor,
        skuId: offlineSku.id,
        futureStockId: futureStock.id,
        orderItemId: offlineSku.itemId,
        stockAmount: futureStock.totalAmount,
        deliveryWindowId: this.deliveryWindowId,
        orderItemQuantity: offlineSku.itemQuantity,
        orderItemKitId: offlineSku.itemId && !!kit ? kit.id : undefined,
        endDate: futureStock.endDate || new Date(),
        availability: AvailabilityEnum.FUTURE_STOCK,
        availableAmount: futureStock.availableAmount,
        availableForReadyForDeliveryWithoutStock: true,
        startDate: futureStock.startDate || new Date(),
        productSizeActive: offlineSku.productSizeActive,
        size: productSizes.find((size) => size.id === offlineSku.sizeId)!,
      };
    });

    return {
      hasValidPacks,
      orderProductPack,
      deliveryWindow,
      skus: futureStockSkuDTOs,
      type: AvailabilityEnum.FUTURE_STOCK,
      allowChangePreOrderGrid: productColor.allowChangePreOrderGrid,
    };
  }

  private async buildReadyForDeliveryProductDTO(): Promise<ProductReadyForDeliveryDTO> {
    const {
      offlineSkus,
      productColor,
      productColorPriceInfo,
      productSizes,
      hasValidPacks,
      orderProductPack,
    } = await this.getArtifacts();

    const stocks = await offlineStocksRepository.findByProductColorId({
      productColorId: this.productColorId,
    });

    const kit = this.kitId ? await offlineKitsRepository.getById(this.kitId) : undefined;
    const gridSkuKits = kit ? this.getGridSkuKits(kit) : [];

    const stockSkuDTOs = offlineSkus.map<ReadyForDeliverySkuDTO>((offlineSku) => {
      const existentStock = stocks.find((stock) => stock.skuId === offlineSku.id);

      const available = !!existentStock && existentStock.availableAmount > 0;

      const stock = existentStock || {
        id: 'some-id',
        totalAmount: 0,
        availableAmount: 0,
        endDate: new Date(),
        skuId: offlineSku.id,
        startDate: new Date(),
        productId: offlineSku.productId,
        productColorId: this.productColorId,
      };
      const prices = OfflineSkuPriceHandler.getPricesBySize(
        productColorPriceInfo,
        offlineSku.sizeId,
      );

      const skuKit = this.getSkuKitFromSkuId({
        skuId: offlineSku.id,
        skuKits: gridSkuKits,
        kit: kit,
      });

      return {
        skuKits: skuKit,
        orderRetailPrice: prices?.retailPrice,
        priceComposition: prices?.priceComposition,
        orderWholeSalePrice: prices?.wholesalePrice,
        orderPromotionalPrice: prices?.promotionalPrice,
        available: available,
        availableForSale: available,
        settledQuantity: 0,
        pendingQuantity: 0,
        reservedQuantity: 0,
        canceledQuantity: 0,
        color: productColor,
        skuId: offlineSku.id,
        stockId: stock.id,
        orderItemId: offlineSku.itemId,
        stockAmount: stock.totalAmount,
        orderItemQuantity: offlineSku.itemQuantity,
        orderItemKitId: offlineSku.itemId && !!kit ? kit.id : undefined,
        endDate: stock.endDate || new Date(),
        availability: AvailabilityEnum.READY_FOR_DELIVERY,
        availableAmount: stock.availableAmount,
        startDate: stock.startDate || new Date(),
        productSizeActive: offlineSku.productSizeActive,
        whenOutOfStock: 'HIDE' as const,
        availableForReadyForDeliveryWithoutStock: true,
        size: productSizes.find((size) => size.id === offlineSku.sizeId)!,
      };
    });

    return {
      hasValidPacks,
      orderProductPack,
      skus: stockSkuDTOs,
      type: AvailabilityEnum.READY_FOR_DELIVERY,
      allowChangeImmediateGrid: productColor.allowChangeImmediateGrid,
    };
  }

  private async buildInfinitePreOrderProductDTO(): Promise<InfinitePreOrderProductDTO> {
    const {
      deliveryWindow,
      offlineSkus,
      productColor,
      productColorPriceInfo,
      productSizes,
      hasValidPacks,
      orderProductPack,
    } = await this.getArtifacts();

    if (!deliveryWindow || !this.deliveryWindowId) {
      throw new Error('delivery window not found');
    }

    const kit = this.kitId ? await offlineKitsRepository.getById(this.kitId) : undefined;
    const gridSkuKits = kit ? this.getGridSkuKits(kit) : [];

    const preOrderSkuDTOs = offlineSkus.map<InfinitePreOrderSkuDTO>((offlineSku) => {
      const isPreOrderAvailable =
        offlineSku.availableForPreOrder &&
        !!offlineSku.preOrderDeliveryWindowIds?.includes(deliveryWindow.id);

      const prices = OfflineSkuPriceHandler.getPricesBySize(
        productColorPriceInfo,
        offlineSku.sizeId,
      );
      const skuKit = this.getSkuKitFromSkuId({
        skuId: offlineSku.id,
        skuKits: gridSkuKits,
        kit: kit,
      });

      return {
        skuKits: skuKit,
        orderRetailPrice: prices?.retailPrice,
        priceComposition: prices?.priceComposition,
        orderWholeSalePrice: prices?.wholesalePrice,
        orderPromotionalPrice: prices?.promotionalPrice,
        available: isPreOrderAvailable,
        deliveryWindowAvailable: isPreOrderAvailable,
        availableForPreOrder: isPreOrderAvailable ? offlineSku.availableForPreOrder : false,
        settledQuantity: 0,
        pendingQuantity: 0,
        reservedQuantity: 0,
        canceledQuantity: 0,
        color: productColor,
        skuId: offlineSku.id,
        orderItemId: offlineSku.itemId,
        deliveryWindowId: this.deliveryWindowId!,
        availability: AvailabilityEnum.INFINITE_PRE_ORDER,
        orderItemQuantity: offlineSku.itemQuantity,
        orderItemKitId: offlineSku.itemId && !!kit ? kit.id : undefined,
        productSizeActive: offlineSku.productSizeActive,
        size: productSizes.find((size) => size.id === offlineSku.sizeId)!,
        preOrderEndDate: offlineSku.preOrderEndDate || new Date(),
        preOrderStartDate: offlineSku.preOrderStartDate || new Date(),
        preOrderMinimumQuantity: offlineSku.preOrderMinimumQuantity || 0,
      };
    });

    return {
      hasValidPacks,
      deliveryWindow,
      orderProductPack,
      skus: preOrderSkuDTOs,
      type: AvailabilityEnum.INFINITE_PRE_ORDER,
      allowChangePreOrderGrid: productColor.allowChangePreOrderGrid,
    };
  }

  private async buildEmptyProductDTO(): Promise<ProductEmptyDTO> {
    const { offlineSkus, productColor, productColorPriceInfo, productSizes } =
      await this.getArtifacts();

    const emptySkuDTOs = offlineSkus.map<EmptySkuDTO>((offlineSku) => {
      const prices = OfflineSkuPriceHandler.getPricesBySize(
        productColorPriceInfo,
        offlineSku.sizeId,
      );

      return {
        skuKits: [],
        orderRetailPrice: prices?.retailPrice,
        priceComposition: prices?.priceComposition,
        orderWholeSalePrice: prices?.wholesalePrice,
        orderPromotionalPrice: prices?.promotionalPrice,
        available: true,
        availableForSale: true,
        settledQuantity: 0,
        pendingQuantity: 0,
        reservedQuantity: 0,
        canceledQuantity: 0,
        color: productColor,
        skuId: offlineSku.id,
        orderItemId: offlineSku.itemId,
        orderItemQuantity: offlineSku.itemQuantity,
        availability: AvailabilityEnum.EMPTY,
        productSizeActive: offlineSku.productSizeActive,
        size: productSizes.find((size) => size.id === offlineSku.sizeId)!,
      };
    });

    return {
      skus: emptySkuDTOs,
      type: AvailabilityEnum.EMPTY,
    };
  }

  private async extractDataFromOrder(
    orderProduct: OfflineOrderProduct,
  ): Promise<OfflineGridBuilderArtifacts> {
    const skusWithItemId = orderProduct.grid.map<OfflineDatabaseSkuWithItemId>((gridItem) => {
      return {
        ...gridItem.sku,
        itemId: gridItem.id,
        itemQuantity: gridItem.quantity,
      };
    });

    const sizes = orderProduct.grid.map((gridItem) => gridItem.size);

    const loadedProductColorWithAllPacks = await offlineProductColorsRepository.getById(
      orderProduct.productColor.id,
    );

    const offlineProductColor = loadedProductColorWithAllPacks || orderProduct.productColor;

    const productColor = OfflineProductColorTransformers.toProductColor(offlineProductColor);
    const deliveryWindow = orderProduct.deliveryWindow
      ? OfflineDeliveryWindowTransformers.toDeliveryWindow(orderProduct.deliveryWindow)
      : undefined;

    const packsToAvailability = getOfflineProductColorPacksBasedOnAvailability(
      offlineProductColor,
      this.availability,
    );

    return {
      productColor,
      deliveryWindow,
      offlineSkus: skusWithItemId,
      hasValidPacks: packsToAvailability.length > 0,
      productSizes: sizes.map(OfflineSizeTransformers.toProductSize),
      productColorPriceInfo: orderProduct.metadata.productColorPriceInfo,
      orderProductPack: OfflineOrderProductTransformers.toOrderProductSelectedPack(orderProduct),
    };
  }

  private getOrderProduct() {
    if (!this.order) {
      return;
    }

    return this.order.getOrderProduct({
      availability: this.availability,
      productColorId: this.productColorId,
      deliveryWindowId: this.deliveryWindowId,
    });
  }

  private async getArtifacts(): Promise<OfflineGridBuilderArtifacts> {
    const orderProduct = this.getOrderProduct();

    if (this.order && orderProduct) {
      return this.extractDataFromOrder(orderProduct);
    }

    if (!this.order && !this.priceTableId) {
      throw new Error('price table id or order is required');
    }

    if (isPreOrder(this.availability) && !this.deliveryWindowId) {
      throw new Error('delivery window id is required for pre order');
    }

    const offlineProductColor = await offlineProductColorsRepository.getById(this.productColorId);

    if (!offlineProductColor) {
      throw new Error('offlineProductColor not found');
    }

    const priceTableId = (this.order?.priceTable.id || this.priceTableId)!;

    const [offlineDeliveryWindow, offlineSkus, productColorPriceInfo] = await Promise.all([
      this.deliveryWindowId
        ? await offlineDeliveryWindowsRepository.getById(this.deliveryWindowId)
        : undefined,
      await offlineSkusRepository.findByProductColorId(offlineProductColor.id),
      await offlineProductColorPriceInfoRepository.getById(offlineProductColor.id, priceTableId),
    ]);

    if (!productColorPriceInfo || (isPreOrder(this.availability) && !offlineDeliveryWindow)) {
      throw new Error('delivery window not found or product color price info not found');
    }

    const sizeIds = offlineSkus.map((sku) => sku.sizeId);
    const offlineSizes = sizeIds.length ? await offlineSizesRepository.findByIds(sizeIds) : [];
    const productSizes = offlineSizes.map(OfflineSizeTransformers.toProductSize);

    if (sizeIds.length !== offlineSkus.length) {
      throw new Error('size not found');
    }

    const deliveryWindow = offlineDeliveryWindow
      ? OfflineDeliveryWindowTransformers.toDeliveryWindow(offlineDeliveryWindow)
      : undefined;

    const productColor = OfflineProductColorTransformers.toProductColor(offlineProductColor);

    const sortedSkus = offlineSkus.sort((a, b) => {
      const sizeA = productSizes.find((size) => size.id === a.sizeId);
      const sizeB = productSizes.find((size) => size.id === b.sizeId);

      if (!sizeA || !sizeB) {
        return 0;
      }

      return sizeA.position - sizeB.position;
    });

    const packsToAvailability = getOfflineProductColorPacksBasedOnAvailability(
      offlineProductColor,
      this.availability,
    );

    return {
      productColor,
      productSizes,
      deliveryWindow,
      productColorPriceInfo,
      offlineSkus: sortedSkus,
      hasValidPacks: packsToAvailability.length > 0,
    };
  }
}
